import { useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from 'pink-lava-ui';
import nabatiLogo from '@/assets/nabati_red_icon.svg';
import type { MenuItemType } from 'pink-lava-ui/types';
import { BookOpenCheck, BookOpenText, IdCard, Truck } from 'lucide-react';
import { Drawer } from 'antd';
import { useUIStore } from '@/stores/uiStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useMemo } from 'react';

function CustomSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileSidebarOpen, closeMobileSidebar } = useUIStore();
  const { isMdAndUp } = useBreakpoint();
  const menuKeyToPath = {
    'on-demand': '/on-demand',
    'booking-list': '/booking-list',
    vehicle: '/vehicle',
    driver: '/driver',
    'on-demand/list': '/on-demand/list',
    'on-demand/history': '/on-demand/history',
  };

  // Create reverse mapping from path to menu key
  const pathToMenuKey = Object.fromEntries(Object.entries(menuKeyToPath).map(([key, path]) => [path, key]));

  // Get current selected key based on pathname
  // This function handles both exact matches and nested routes
  const currentSelectedKey = useMemo(() => {
    // First try exact match
    const exactMatch = pathToMenuKey[location.pathname];
    if (exactMatch) return exactMatch;

    // For nested routes, find the best matching menu key
    const pathSegments = location.pathname.split('/').filter(Boolean);

    let selectedKey;

    // Try to match from most specific to least specific
    // e.g., /on-demand/list/123 -> try /on-demand/list first, then /on-demand
    for (let i = pathSegments.length; i >= 1; i--) {
      const testPath = `/${pathSegments.slice(0, i).join('/')}`;
      const match = pathToMenuKey[testPath];
      if (match) {
        selectedKey = match;
        break;
      }
    }

    return selectedKey;
  }, [location.pathname, pathToMenuKey]);

  const handleSidebarClick = (menuItem: any) => {
    const path = menuKeyToPath[menuItem.key as keyof typeof menuKeyToPath];
    if (path) {
      navigate(path);
      // Close mobile sidebar when navigating
      closeMobileSidebar();
    } else {
      console.log('Tidak ada route yang didefinisikan untuk key:', menuItem.key);
    }
  };

  const sidebarMenu: MenuItemType[] = [
    ...(isMdAndUp
      ? ([
          {
            label: <div className="w-full text-center">DC Trans Vendor</div>,
            type: 'group',
          },
          {
            type: 'divider',
          },
        ] as MenuItemType[])
      : []),
    {
      key: 'on-demand',
      icon: <BookOpenCheck size={20} />,
      label: 'Penugasan On Demand',
      children: [
        {
          key: 'on-demand/list',
          label: 'Penugasan',
        },
        {
          key: 'on-demand/history',
          label: 'Riwayat',
        },
      ],
    },
    {
      key: 'booking-list',
      icon: <BookOpenText size={20} />,
      label: 'Daftar Booking',
    },
    {
      type: 'divider',
    },
    {
      label: 'Masterdata',
      type: 'group',
      children: [
        {
          key: 'vehicle',
          icon: <Truck size={20} />,
          label: 'Kendaraan',
        },
        {
          key: 'driver',
          icon: <IdCard size={20} />,
          label: 'Pengemudi',
        },
      ],
    },
  ];

  const sidebarContent = (
    <Sidebar
      title="DC Trans Vendor"
      logo={
        <div className="flex items-center justify-center pt-4">
          <img className="w-26! h-18! cursor-pointer " onClick={() => navigate('/')} src={nabatiLogo} />
        </div>
      }
      items={sidebarMenu}
      onClick={handleSidebarClick}
      selectedKeys={currentSelectedKey ? [currentSelectedKey] : []}
      // openKeys={currentSelectedKey?.includes('/') ? [currentSelectedKey.split('/')[0]] : undefined}
    />
  );

  return (
    <>
      {/* Desktop Sidebar */}
      {isMdAndUp && <div>{sidebarContent}</div>}

      {/* Mobile Drawer */}
      {!isMdAndUp && (
        <Drawer
          title={
            <div className="flex flex-row gap-1 items-center">
              DC Trans Vendor
              <img src={nabatiLogo} className="w-7! h-7!" />
            </div>
          }
          placement="left"
          onClose={closeMobileSidebar}
          open={isMobileSidebarOpen}
          width={256}
          classNames={{
            body: 'p-0! main-layout-container',
            wrapper: 'h-dvh!',
          }}
        >
          {sidebarContent}
        </Drawer>
      )}
    </>
  );
}

export default CustomSidebar;
